/**
 * DHS Unit Inspection Form — official checklist preset.
 *
 * Mirrors the NYC DHS shelter unit inspection paper form (checklist +
 * header/footer). Instructions pages from the PDF are omitted. Admins
 * clone this draft, customize dropdown options, and publish.
 */

import type { InspectionPreset } from './inspection-constants.js';
import type { InspectionQuestion, InspectionSchema, InspectionSection } from './inspection-constants.js';

function qid(n: number): string {
  return `01900001-0000-7000-8000-${n.toString(16).padStart(12, '0')}`;
}

const RATING_OPTIONS = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
] as const;

function yesNoChecklist(label: string, id: string): InspectionQuestion {
  return {
    id,
    kind: 'yes_no',
    label,
    required: true,
    allowNa: true,
    defectIf: 'no',
  };
}

function checklistSection(
  sectionId: string,
  title: string,
  items: ReadonlyArray<{ num: string; text: string; id: string }>,
): InspectionSection {
  return {
    id: sectionId,
    title,
    questions: items.map((item) =>
      yesNoChecklist(`${item.num}. ${item.text}`, item.id),
    ),
  };
}

const headerSection: InspectionSection = {
  id: qid(1),
  title: 'Inspection Information',
  questions: [
    {
      id: qid(2),
      kind: 'short_text',
      label: 'Client Name',
      required: false,
      questionKey: 'header.client_name',
    },
    {
      id: qid(3),
      kind: 'short_text',
      label: 'Unit No',
      required: true,
      prefillSource: 'unit.number',
      questionKey: 'header.unit_number',
    },
    {
      id: qid(4),
      kind: 'short_text',
      label: 'Date of Inspection',
      required: true,
      prefillSource: 'inspection.date',
      questionKey: 'header.inspection_date',
    },
    {
      id: qid(5),
      kind: 'short_text',
      label: 'Case Number',
      required: false,
      questionKey: 'header.case_number',
    },
    {
      id: qid(6),
      kind: 'short_text',
      label: 'Shelter Name',
      required: true,
      prefillSource: 'building.name',
      questionKey: 'header.shelter_name',
    },
    {
      id: qid(7),
      kind: 'short_text',
      label: 'Address',
      required: true,
      prefillSource: 'building.address',
      questionKey: 'header.address',
    },
    {
      id: qid(8),
      kind: 'number',
      label: 'Family Composition — Adults',
      min: 0,
      max: 99,
      required: false,
      questionKey: 'header.adults',
    },
    {
      id: qid(9),
      kind: 'number',
      label: 'Family Composition — Children',
      min: 0,
      max: 99,
      required: false,
      questionKey: 'header.children',
    },
    {
      id: qid(10),
      kind: 'yes_no',
      label: 'Child under 2',
      required: false,
      defectIf: 'no',
    },
    {
      id: qid(11),
      kind: 'yes_no',
      label: 'Newborn in Unit (0–6 months)',
      required: false,
      defectIf: 'no',
    },
    {
      id: qid(12),
      kind: 'yes_no',
      label: 'Unattended Children in Unit',
      required: true,
      defectIf: 'yes',
    },
    {
      id: qid(13),
      kind: 'yes_no',
      label: 'Unauthorized Guests in Unit',
      required: true,
      defectIf: 'yes',
    },
    {
      id: qid(14),
      kind: 'yes_no',
      label: 'ACS Case',
      required: false,
    },
    {
      id: qid(15),
      kind: 'yes_no',
      label: 'Family Present in Unit',
      required: false,
    },
  ],
};

const infantChildSafety = checklistSection(qid(20), 'Infant / Child Safety', [
  {
    num: '1',
    text: 'Crib/ Pack N Play present for all children 24 months and under?',
    id: qid(21),
  },
  {
    num: '2',
    text: "Crib/ Pack 'n' Play present without damage or defect?",
    id: qid(22),
  },
  {
    num: '3',
    text: 'If Crib: appropriate in size for baby; floor at appropriate height; rails move smoothly up and down; all surfaces free of splinters or peeling paint; stable?',
    id: qid(23),
  },
  {
    num: '4',
    text: 'Crib/ Pack N Play have firm mattress and fitted sheets?',
    id: qid(24),
  },
  { num: '5', text: 'Crib/ Pack N Play free of objects?', id: qid(25) },
  {
    num: '6',
    text: "Crib/ Pack 'n' Play positioned away from windows, radiators, overhanging objects?",
    id: qid(26),
  },
  { num: '7', text: 'Safe Sleep Poster in unit?', id: qid(27) },
  { num: '8', text: 'Mother & newborn observed (If Present)', id: qid(28) },
  {
    num: '9a',
    text: 'Safe Sleep Practices Observed (for infants 0–12 mons.)',
    id: qid(29),
  },
  { num: '9b', text: 'Safe Sleep Discussed with client', id: qid(30) },
  { num: '9c', text: 'Mom demonstrate how to place infant to sleep?', id: qid(31) },
  {
    num: '9d',
    text: "Medications/ Opioids/ Hazardous chemical out of child's reach?",
    id: qid(32),
  },
]);

const healthSafety = checklistSection(qid(40), 'Health & Safety', [
  { num: '9', text: 'Unit free of vermin/ traces of vermin?', id: qid(41) },
  { num: '10', text: 'Unit free of unauthorized pets?', id: qid(42) },
  { num: '11', text: 'Adequate food in unit and properly stored?', id: qid(43) },
  { num: '12', text: 'Unit free of observable mold?', id: qid(44) },
  { num: '13', text: 'Unit free of smoking, drug use or alcohol?', id: qid(45) },
  {
    num: '14',
    text: 'Housekeeping; unit free of clutter & excess items?',
    id: qid(46),
  },
]);

const fireSafety = checklistSection(qid(50), 'Fire Safety', [
  {
    num: '15',
    text: 'Unit has functioning smoke and carbon monoxide detectors?',
    id: qid(51),
  },
  {
    num: '16',
    text: 'All exits free of obstructions or barriers to immediate exit to unit?',
    id: qid(52),
  },
  { num: '17', text: 'Fire Safety Notice Posted on unit entrance door?', id: qid(53) },
  { num: '18', text: 'Fire Safety egress signage posted?', id: qid(54) },
]);

const electrical = checklistSection(qid(60), 'Electrical', [
  { num: '19', text: 'Electrical outlets functional and free of defect?', id: qid(61) },
  { num: '20', text: 'Functional GFI Outlets where required?', id: qid(62) },
  { num: '21', text: 'Unit free of exposed wiring?', id: qid(63) },
  { num: '22', text: 'Outlets free of excess wires and not overloaded?', id: qid(64) },
  {
    num: '23',
    text: 'Unit light fixtures functional, secure and have a working bulb?',
    id: qid(65),
  },
]);

const heatVentilation = checklistSection(qid(70), 'Heat / Ventilation', [
  { num: '24', text: 'Adequate heat available from all heat sources?', id: qid(71) },
  { num: '25', text: 'Heat sources functioning properly?', id: qid(72) },
  { num: '26', text: 'Radiator covers/ heater covers intact?', id: qid(73) },
]);

const kitchenBathroom = checklistSection(qid(80), 'Kitchen / Bathroom', [
  {
    num: '27',
    text: 'Adequate Hot & Cold Water available from all fixtures?',
    id: qid(81),
  },
  { num: '28', text: 'Unit Ventilation/ exhaust operational and clean?', id: qid(82) },
  { num: '29', text: 'Toilet free of damage & defects?', id: qid(83) },
  { num: '30', text: 'Faucets free of damage & defects?', id: qid(84) },
  { num: '31', text: 'Basins free of damage & defects?', id: qid(85) },
  { num: '32', text: 'Shower/ tub free of damage & defect?', id: qid(86) },
  {
    num: '33',
    text: 'Medicine Cabin Intact and Free of damage & defect?',
    id: qid(87),
  },
]);

const appliances = checklistSection(qid(90), 'Appliances', [
  { num: '34', text: 'Appliances in unit are authorized?', id: qid(91) },
  { num: '35', text: 'Refrigerator free of damage or defect?', id: qid(92) },
  { num: '36', text: 'Stove free of damage or defect?', id: qid(93) },
  { num: '37', text: 'Oven free of damage or defect?', id: qid(94) },
  { num: '38', text: 'Cabinets free of damage or defect?', id: qid(95) },
]);

const furniture = checklistSection(qid(100), 'Furniture', [
  {
    num: '39',
    text: 'Adequate furniture in unit for family composition?',
    id: qid(101),
  },
  { num: '40', text: 'Beds without damage or defect?', id: qid(102) },
  { num: '41', text: 'Chairs without damage or defect?', id: qid(103) },
  { num: '42', text: 'Tables without damage or defect?', id: qid(104) },
  { num: '43', text: 'Dressers without damage or defect?', id: qid(105) },
]);

const windowsDoors = checklistSection(qid(110), 'Windows & Doors', [
  { num: '44', text: 'Window Glass intact and undamaged?', id: qid(111) },
  {
    num: '45',
    text: 'Windows operational and without defect and can lock?',
    id: qid(112),
  },
  { num: '46', text: 'Window Guards present and properly installed?', id: qid(113) },
  {
    num: '47',
    text: 'Window screens in place free of damage and defect?',
    id: qid(114),
  },
  {
    num: '48',
    text: 'Entrance door has no damage/ defect and can lock?',
    id: qid(115),
  },
  { num: '49', text: 'Peephole, knocker, and/or doorbell operational?', id: qid(116) },
]);

const wallsCeilings = checklistSection(qid(120), 'Walls and Ceilings', [
  { num: '50', text: 'Unit walls and ceilings clean?', id: qid(121) },
  {
    num: '51',
    text: 'Unit walls and ceilings painted without chipping / peeling?',
    id: qid(122),
  },
  { num: '52', text: 'Unit walls and ceilings free of water damage?', id: qid(123) },
  { num: '53', text: 'Unit walls and ceilings free of holes and cracks?', id: qid(124) },
]);

const floors = checklistSection(qid(130), 'Floors', [
  { num: '54', text: 'Unit floors free of damage and defects?', id: qid(131) },
  { num: '55', text: 'Unit floors clean? (Housekeeping)', id: qid(132) },
  {
    num: '56',
    text: 'Unit floors free of clutter and trip hazards? (Housekeeping)',
    id: qid(133),
  },
]);

const summarySection: InspectionSection = {
  id: qid(140),
  title: 'Summary & Sign-off',
  questions: [
    {
      id: qid(141),
      kind: 'single_select',
      label: 'House Keeping Skills',
      options: [...RATING_OPTIONS],
      required: false,
      questionKey: 'footer.housekeeping_rating',
    },
    {
      id: qid(142),
      kind: 'yes_no',
      label: 'Housekeeping Issues',
      required: false,
      defectIf: 'yes',
      questionKey: 'footer.housekeeping_issues',
    },
    {
      id: qid(143),
      kind: 'yes_no',
      label: 'Unit Repairs Needed',
      required: false,
      defectIf: 'yes',
      questionKey: 'footer.unit_repairs_needed',
      helpText: 'Auto-set to Yes when checklist deficiencies are flagged.',
    },
    {
      id: qid(144),
      kind: 'yes_no',
      label: 'Work Order(s) Submitted',
      required: false,
      questionKey: 'footer.work_orders_submitted',
      helpText: 'Auto-set to Yes when work orders are created from defects.',
    },
    {
      id: qid(145),
      kind: 'short_text',
      label: 'Date Submitted',
      required: false,
      questionKey: 'footer.wo_date_submitted',
    },
    {
      id: qid(146),
      kind: 'single_select',
      label: 'Overall Condition of the unit',
      options: [...RATING_OPTIONS],
      required: false,
      questionKey: 'footer.overall_condition',
    },
    {
      id: qid(147),
      kind: 'single_select',
      label: 'Case Manager',
      options: [],
      optionSource: 'org_users',
      required: false,
    },
    {
      id: qid(148),
      kind: 'signature',
      label: 'Client Signature',
      required: false,
      collectPrintedName: true,
      questionKey: 'footer.client_signature',
    },
    {
      id: qid(149),
      kind: 'signature',
      label: 'Case Manager Signature',
      required: false,
      collectPrintedName: true,
      questionKey: 'footer.case_manager_signature',
    },
    {
      id: qid(150),
      kind: 'long_text',
      label: 'Comments (Include Housekeeping)',
      required: false,
      questionKey: 'footer.comments',
    },
  ],
};

export const DHS_OFFICIAL_SCHEMA: InspectionSchema = {
  layout: 'checklist_table',
  pdfLayout: 'dhs_official',
  pdfLayouts: ['dhs_official', 'dhs_clean'],
  sections: [
    headerSection,
    infantChildSafety,
    healthSafety,
    fireSafety,
    electrical,
    heatVentilation,
    kitchenBathroom,
    appliances,
    furniture,
    windowsDoors,
    wallsCeilings,
    floors,
    summarySection,
  ],
};

export const dhsOfficialPreset: InspectionPreset = {
  slug: 'dhs-unit-inspection-official',
  name: 'DHS Unit Inspection (Official Form)',
  description:
    'Full DHS shelter unit inspection checklist with header, Yes/No/N/A sections, summary, signatures, and printable official-form PDF.',
  appliesTo: 'unit',
  schema: DHS_OFFICIAL_SCHEMA,
};
